import { Box, Checkbox, FormControlLabel, Grid, Stack, Typography } from '@mui/material';
import { MouseEventHandler, useCallback, useEffect, useRef } from 'react';
import {
  required,
  TextInput,
  useDataProvider,
  useInput,
  useNotify,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { B2bCompanyDocumentDetails, PaymentMethodType } from '@Common/types/bo/b2b/companies';

import { ShowSection } from '@sh4res/bo-common/components';
import { SIGNATURE_DELEGATION_VALUES } from '@sh4res/bo-common/components/B2bCompanyPaymentMethods/company_payment_methods/steps/PaymentDetailsStep';
import { useResourceBasePath } from '@sh4res/bo-common/hooks/useResourceBasePath';
import { getErrorMessage } from '@sh4res/bo-common/utils';
import { trimmedEmail } from '@sh4res/bo-common/validators';

const testId = 'BoExternalB2bCompanyPaymentMethodsSignatureStep';

function formatIban(iban: string) {
  const lastFourDigits = iban.replaceAll(' ', '').slice(-4);
  return `•••• ${lastFourDigits}`;
}

type Props = {
  companyId: number;
};

export const SignatureStep = (props: Props) => {
  const { companyId } = props;
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const resourceBasePath = useResourceBasePath();
  const isMountedRef = useRef(true);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const paymentMethodType = useWatch<{ payment_method_type: PaymentMethodType }>({
    name: 'payment_method_type',
  });
  const iban = useWatch<{ iban: string }>({ name: 'iban' });
  const bic = useWatch<{ bic: string }>({ name: 'bic' });
  const signatureDelegation = useWatch<{ signature_delegation?: string }>({
    name: 'signature_delegation',
  });
  const isExternalSignatoryFlow =
    signatureDelegation === SIGNATURE_DELEGATION_VALUES.EXTERNAL_SIGNATORY;
  const { field, id } = useInput({
    source: 'is_sepa_mandate_signed',
    validate: (value) => (isExternalSignatoryFlow || value === true ? undefined : 'is required'),
    defaultValue: false,
    isRequired: !isExternalSignatoryFlow,
  });
  const { setValue } = useFormContext();
  const draftMandateMeta = useWatch({ name: 'sepa_draft_mandate_meta' }) as
    | { requested_at: string; iban: string; bic: string }
    | undefined;
  const isDraftRequested =
    draftMandateMeta?.iban === iban.replaceAll(' ', '') && draftMandateMeta.bic === bic;

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  const postDraftMandate = useCallback(async () => {
    setValue('sepa_draft_mandate_meta', {
      requested_at: new Date().toISOString(),
      iban: iban.replaceAll(' ', ''),
      bic,
    });
    try {
      await dataProvider.create(
        `${resourceBasePath}companies/payment_methods/draft_unsigned_mandate`,
        {
          data: {
            acc_iban: iban.replaceAll(' ', ''),
            acc_bic: bic,
            company_id: companyId,
          },
        },
      );
    } catch (error: unknown) {
      notify(
        translate(
          'resources.bo_common.b2b.companies.payment_methods.sections.signature.sepa_mandate_link.generate_failure',
        ),
        { type: 'error', messageArgs: { error: getErrorMessage(error) }, multiLine: true },
      );
    }
  }, [iban, bic, dataProvider, notify, companyId, resourceBasePath, setValue, translate]);

  useEffect(() => {
    if (isExternalSignatoryFlow || isDraftRequested) {
      return;
    }

    void postDraftMandate();
  }, [postDraftMandate, isDraftRequested, isExternalSignatoryFlow]);

  const fetchMandate = useCallback(async (): Promise<B2bCompanyDocumentDetails | undefined> => {
    const result = await dataProvider.getList<B2bCompanyDocumentDetails>(
      `${resourceBasePath}companies/documents`,
      {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'created_at', order: 'DESC' },
        filter: {
          company_id: companyId,
          document_type: 'b2b_business_sepa_mandate_form_unsigned',
          ...(draftMandateMeta?.requested_at && {
            from_created_at: draftMandateMeta.requested_at,
          }),
        },
      },
    );
    return result.data[0];
  }, [companyId, dataProvider, resourceBasePath, draftMandateMeta]);

  const handleMandateClick: MouseEventHandler<HTMLSpanElement> = useCallback(
    async (event) => {
      event.stopPropagation();
      event.preventDefault();
      try {
        const firstDoc = await fetchMandate();
        if (firstDoc?.document_url && isDraftRequested) {
          window.open(firstDoc.document_url, '_blank');
        } else {
          notify(
            translate(
              'resources.bo_common.b2b.companies.payment_methods.sections.signature.sepa_mandate_link.loading',
            ),
            { type: 'info' },
          );
          if (!pollingIntervalRef.current) {
            pollingIntervalRef.current = setInterval(async () => {
              try {
                const doc = await fetchMandate();
                if (doc?.document_url && isMountedRef.current) {
                  stopPolling();
                  window.open(doc.document_url, '_blank');
                }
              } catch {
                stopPolling();
              }
            }, 3000);
          }
        }
      } catch (error: unknown) {
        notify(
          translate(
            'resources.bo_common.b2b.companies.payment_methods.sections.signature.sepa_mandate_link.fetch_failure',
          ),
          { type: 'error', messageArgs: { error: getErrorMessage(error) }, multiLine: true },
        );
      }
    },
    [fetchMandate, notify, translate, stopPolling, isDraftRequested],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.companies.payment_methods.sections.signature.billing_title"
        noCard>
        <Grid container gap="16px" display="flex" alignItems="start" direction="column">
          <Grid item>
            <Typography variant="caption" color="text.secondary" display="block">
              {translate(
                'resources.bo_common.b2b.companies.payment_methods.sections.signature.iban',
              )}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {iban ? formatIban(iban) : ''}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="caption" color="text.secondary" display="block">
              {translate(
                'resources.bo_common.b2b.companies.payment_methods.sections.signature.bic',
              )}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {bic || ''}
            </Typography>
          </Grid>
        </Grid>
      </ShowSection>

      {paymentMethodType === 'sepa_direct_debit' && !isExternalSignatoryFlow && (
        <ShowSection
          leftAligned
          label="resources.bo_common.b2b.companies.payment_methods.sections.signature.acknowledgements_title"
          noCard>
          <FormControlLabel
            label={
              <Stack spacing={0.5}>
                <Typography variant="body1" fontWeight={600}>
                  {translate(
                    'resources.bo_common.b2b.companies.payment_methods.sections.signature.subheader',
                  )}
                </Typography>
                <Typography variant="body1">
                  {translate(
                    'resources.bo_common.b2b.companies.payment_methods.sections.signature.sepa_mandate_body_start',
                  )}
                  <Typography
                    variant="body1"
                    component="span"
                    onClick={handleMandateClick}
                    sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}>
                    {translate(
                      'resources.bo_common.b2b.companies.payment_methods.sections.signature.subheader',
                    )}
                  </Typography>
                  {translate(
                    'resources.bo_common.b2b.companies.payment_methods.sections.signature.sepa_mandate_body_end',
                  )}
                </Typography>
              </Stack>
            }
            control={
              <Checkbox
                id={id}
                {...field}
                onChange={(event) => field.onChange(event.target.checked)}
                sx={{ alignSelf: 'flex-start' }}
                data-testid={`${testId}_SepaMandateCheckbox`}
              />
            }
          />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {translate(
              'resources.bo_common.b2b.companies.payment_methods.sections.signature.electronic_signature_note',
            )}
          </Typography>
        </ShowSection>
      )}

      {paymentMethodType === 'sepa_direct_debit' && isExternalSignatoryFlow && (
        <ShowSection
          leftAligned
          label="resources.bo_common.b2b.companies.payment_methods.sections.signature.acknowledgements_title"
          noCard>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {translate(
              'resources.bo_common.b2b.companies.payment_methods.sections.signature.external_signatory.intro',
            )}
          </Typography>
          <TextInput
            source="external_signatory_lastname"
            label="resources.bo_common.b2b.companies.payment_methods.sections.signature.external_signatory.lastname"
            validate={[required()]}
            isRequired
            fullWidth
            data-testid={`${testId}_LastNameInput`}
          />
          <TextInput
            source="external_signatory_firstname"
            label="resources.bo_common.b2b.companies.payment_methods.sections.signature.external_signatory.firstname"
            validate={[required()]}
            isRequired
            fullWidth
            data-testid={`${testId}_FirstNameInput`}
          />
          <TextInput
            source="external_signatory_email"
            label="resources.bo_common.b2b.companies.payment_methods.sections.signature.external_signatory.email"
            type="email"
            validate={[required(), trimmedEmail()]}
            isRequired
            fullWidth
            data-testid={`${testId}_EmailInput`}
          />
        </ShowSection>
      )}
    </Box>
  );
};
